import { useMutation } from '@tanstack/react-query';
import {
  postPasswordResetConfirm,
  postPasswordResetRequest,
  postPasswordResetVerify,
} from '../../infrastructure/api/authApi';
import type {
  PasswordResetConfirmDto,
  PasswordResetRequestDto,
  PasswordResetVerifyDto,
} from '../../domain/contracts/api.contracts';

/** Error codes the backend puts in the problem-details `code` field (API_CONTRACT §1.3). */
export type PasswordResetErrorCode =
  | 'VALIDATION_FAILED'
  | 'OTP_INVALID'
  | 'OTP_EXPIRED'
  | 'RESET_LOCKED'
  | 'RESET_TOKEN_INVALID'
  | 'PASSWORD_POLICY'
  | 'RATE_LIMITED'
  | 'FEATURE_DISABLED';

export interface PasswordResetFailure {
  status?: number;
  code?: PasswordResetErrorCode;
  attemptsRemaining?: number;
  /** From the problem body, falling back to the `Retry-After` header. */
  retryAfterSeconds?: number;
}

/** Reads the typed failure out of an axios error. Never touches the request body, so nothing secret is echoed. */
export function passwordResetFailureOf(error: unknown): PasswordResetFailure {
  const res = (error as { response?: { status?: number; data?: Record<string, unknown>; headers?: Record<string, unknown> } })
    ?.response;
  if (!res) return {};
  const data = res.data ?? {};
  const header = Number(res.headers?.['retry-after']);
  const body = typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : undefined;
  return {
    status: res.status,
    code: typeof data.code === 'string' ? (data.code as PasswordResetErrorCode) : undefined,
    attemptsRemaining: typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : undefined,
    retryAfterSeconds: body ?? (Number.isFinite(header) && header > 0 ? header : undefined),
  };
}

/** E30 — request a code. */
export function usePasswordResetRequest() {
  return useMutation({ mutationFn: (body: PasswordResetRequestDto) => postPasswordResetRequest(body) });
}

/** E31 — verify the code. */
export function usePasswordResetVerify() {
  return useMutation({ mutationFn: (body: PasswordResetVerifyDto) => postPasswordResetVerify(body) });
}

/** E32 — set the new password. */
export function usePasswordResetConfirm() {
  return useMutation({ mutationFn: (body: PasswordResetConfirmDto) => postPasswordResetConfirm(body) });
}
